import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { usePWA } from '@/hooks/usePWA';
import { RippleButton } from '@/components/interactions';
import { Card } from '@/shared/ui/card';
import { Download, X } from 'lucide-react';

export const UpdateBanner = () => {
  const { t } = useTranslation();
  const { updateAvailable, applyUpdate } = usePWA();

  if (!updateAvailable) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 bg-primary text-primary-foreground flex items-center justify-center gap-3 px-4 py-3 shadow-lg"
      style={{ zIndex: 9999 }}
    >
      <span className="text-sm font-medium">🔄 {t('pwa.updateAvailable', 'New version available')}</span>
      <button
        onClick={applyUpdate}
        className="px-3 py-1 text-sm font-semibold rounded bg-white/20 hover:bg-white/30 active:scale-95 transition-all"
      >
        {t('pwa.update', 'Update')}
      </button>
    </div>
  );
};

export const InstallPrompt = () => {
  const { t } = useTranslation();
  const { isInstallable, isInstalled, promptInstall } = usePWA();
  const [dismissed, setDismissed] = useState(false);

  // ... keep existing code

  useEffect(() => {
    const wasDismissed = localStorage.getItem('install-prompt-dismissed');
    if (wasDismissed === 'true') {
      setDismissed(true);
    }
  }, []);

  const handleInstall = async () => {
    const accepted = await promptInstall();
    if (accepted) {
      setDismissed(true);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('install-prompt-dismissed', 'true');
  };

  if (!isInstallable || isInstalled || dismissed) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-40 animate-slide-up md:left-auto md:right-4 md:max-w-sm">
      <Card className="p-4 shadow-lg">
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-muted-foreground hover:text-foreground"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-start gap-3 pr-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <Download className="w-5 h-5 text-primary" />
          </div>

          <div className="flex-1">
            <h3 className="font-semibold text-foreground text-sm mb-1">
              {t('pwa.installTitle', 'Install App')}
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              {t('pwa.installDescription', 'Add to home screen for quick access and offline use')}
            </p>

            <div className="flex gap-2">
              <RippleButton onClick={handleInstall} size="sm" className="flex-1">
                {t('pwa.install', 'Install')}
              </RippleButton>
              <RippleButton onClick={handleDismiss} size="sm" variant="ghost">
                {t('pwa.notNow', 'Not Now')}
              </RippleButton>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};